import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import type { MeetingTypeOption } from '@/lib/withCurrentTemplate.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingTemplateSwitchDialogProps {
  /** The gesprekstype the user picked, or null when nothing is being switched. */
  template: MeetingTypeOption | null;
  failed: boolean;
  pending: boolean;
  onChoose: (recreate: boolean) => void;
  onCancel: () => void;
}

/**
 * The question every gesprekstype switch asks: write the topics again with the
 * new structure, or only change the structure?
 *
 * It is asked rather than assumed, because a preparation the user has already
 * worked on must never be re-created behind their back. Either answer keeps
 * every line they wrote or edited — only Wundo's own untouched suggestions
 * belong to the structure they were written for.
 */
export function MeetingTemplateSwitchDialog({
  template,
  failed,
  pending,
  onChoose,
  onCancel,
}: MeetingTemplateSwitchDialogProps) {
  return (
    <Dialog open={template !== null} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>
            {m.meeting_prep_type_switch_title({ name: template?.name ?? '' })}
          </DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-2.5 px-5 py-4'>
          <p className='text-sm text-ink-body'>{m.meeting_prep_type_switch_body()}</p>

          <Button
            shape='block'
            fullWidth
            disabled={pending}
            onClick={() => onChoose(true)}
          >
            {m.meeting_prep_type_switch_recreate()}
          </Button>
          <Button
            shape='block'
            variant='quiet'
            fullWidth
            disabled={pending}
            onClick={() => onChoose(false)}
          >
            {m.meeting_prep_type_switch_structure()}
          </Button>

          {failed && (
            <p role='alert' className='text-sm text-danger'>
              {m.meeting_action_failed()}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={onCancel}>
            {m.meeting_cancel()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
